import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../theme/ThemeContext';
import { useContactForm } from '../context/ContactFormContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from '../constants/legalLinks';
import ReportIntakeModal from '../components/ReportIntakeModal';
import PrivacyChoicesButton from '../components/PrivacyChoicesButton';
import { trackGoogleAdsDiagnostic, withStoredClickIds } from '../utils/googleAds';
import { 
  Search, 
  BarChart3, 
  Globe, 
  Zap, 
  Shield, 
  Users, 
  ArrowRight,
  Check,
  Sparkles,
  Filter,
  Download,
  Mail,
  Building2,
  Calendar,
  Sun,
  Moon,
  User,
  LayoutDashboard,
  TrendingUp,
  Newspaper,
  Menu,
  X,
  FileText,
} from 'lucide-react';

const MOB = {
  navPadding: '14px 16px',
  logoSize: 40,
  heroPadding: '48px 20px',
  heroTitleSize: '32px',
  heroSubSize: '16px',
  sectionPadding: '48px 20px',
  sectionTitleSize: '28px',
  cardPadding: '20px',
  footerPadding: '28px 20px',
};

function LandingPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading } = useAuth();
  const { theme, darkMode, toggleTheme } = useTheme();
  const { openContactForm } = useContactForm();
  const isMobile = useIsMobile();
  const displayName = (user?.name || user?.email?.split('@')[0] || 'Account').replace(/\b\w/g, (c) => c.toUpperCase());

  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [navOpen, setNavOpen] = useState(false);
  const [showReportIntake, setShowReportIntake] = useState(false);
  const cardRefs = useRef([]);

  const features = [
    {
      icon: Search,
      title: 'AI-Powered Research',
      description: 'Automatically gather competitor data using advanced AI. No manual research needed.',
      gradient: 'from-blue-500 to-cyan-400',
      color: '#3b82f6',
      stats: '10x faster research'
    },
    {
      icon: BarChart3,
      title: 'Price History Tracking',
      description: 'Visualize pricing trends over time with interactive charts spanning up to 10 years.',
      gradient: 'from-emerald-500 to-teal-400',
      color: '#10b981',
      stats: '10 years of data'
    },
    {
      icon: Globe,
      title: 'Global Market Data',
      description: 'Get location-specific insights for any city, state, or country worldwide.',
      gradient: 'from-violet-500 to-purple-400',
      color: '#8b5cf6',
      stats: '190+ countries'
    },
    {
      icon: Zap,
      title: 'Sentiment Analysis',
      description: 'Compare customer sentiment and reviews across all your competitors at a glance.',
      gradient: 'from-amber-500 to-orange-400',
      color: '#f59e0b',
      stats: 'Real-time analysis'
    },
    {
      icon: Filter,
      title: 'Smart Filtering',
      description: 'Filter by price range, sentiment score, and more to find your ideal competitors.',
      gradient: 'from-rose-500 to-pink-400',
      color: '#f43f5e',
      stats: '20+ filters'
    },
    {
      icon: Download,
      title: 'Export & Share',
      description: 'Export interactive HTML reports or JSON data for further analysis.',
      gradient: 'from-cyan-500 to-blue-400',
      color: '#06b6d4',
      stats: 'HTML & JSON'
    },
    {
      icon: TrendingUp,
      title: 'SEO Intelligence',
      description: 'Track keyword opportunities, surface SEO gaps, and prioritize content moves against competitors.',
      gradient: 'from-indigo-500 to-blue-400',
      color: '#6366f1',
      stats: 'Rank tracking'
    },
    {
      icon: Newspaper,
      title: 'Live News',
      description: 'Use current events and breaking news to inform your research. Stay ahead with real-time context that shapes markets and demand.',
      gradient: 'from-slate-600 to-slate-500',
      color: '#475569',
      stats: 'Real-time context'
    }
  ];

  const pricingFeatures = [
    'Unlimited market research queries',
    'Up to 10 years of price history',
    'Multi-tab research sessions',
    'Export to HTML & JSON',
    'Competitor comparison tools',
    'Sentiment analysis',
  ];

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: theme.bg, color: theme.text }}>
      {/* Navigation */}
      <nav style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: isMobile ? MOB.navPadding : '20px 40px',
        borderBottom: `1px solid ${theme.border}`,
        backgroundColor: theme.card,
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ cursor: 'pointer' }} onClick={() => { navigate('/'); setNavOpen(false); }}>
            <img
              src="/logo.png"
              alt="Market Scan"
              style={{
                width: isMobile ? MOB.logoSize : 128,
                height: isMobile ? MOB.logoSize : 128,
                objectFit: 'contain',
                borderRadius: isMobile ? '8px' : '12px',
                display: 'block',
                backgroundColor: 'transparent',
              }}
            />
          </div>
          {isMobile && (
            <span style={{ marginLeft: '10px', fontSize: '18px', fontWeight: '700', color: theme.text }}>Market Scan</span>
          )}
        </div>

        {isMobile ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              onClick={toggleTheme}
              style={{
                padding: '8px',
                backgroundColor: theme.primaryLight,
                color: theme.primary,
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title={darkMode ? 'Light mode' : 'Dark mode'}
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button
              type="button"
              onClick={() => setNavOpen(!navOpen)}
              style={{
                padding: '8px',
                backgroundColor: navOpen ? theme.primary : theme.primaryLight,
                color: navOpen ? theme.primaryContrast : theme.primary,
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              aria-label={navOpen ? 'Close menu' : 'Open menu'}
            >
              {navOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              type="button"
              onClick={toggleTheme}
              style={{
                padding: '8px',
                backgroundColor: theme.primaryLight,
                color: theme.primary,
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title={darkMode ? 'Light mode' : 'Dark mode'}
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            {(loading ? false : isAuthenticated) ? (
              <>
                <button
                  onClick={() => navigate('/profile')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 14px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: theme.text,
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: '8px',
                  }}
                  title="Profile"
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: theme.primaryLight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <User size={16} color={theme.primary} />
                  </div>
                  <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {displayName}
                  </span>
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: theme.primaryContrast,
                    backgroundColor: theme.primary,
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  <LayoutDashboard size={18} />
                  Dashboard
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/login')}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: theme.textMuted,
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  Log In
                </button>
                <button
                  onClick={() => { trackGoogleAdsDiagnostic('signup_cta_click', { location: 'landing_page' }); navigate(withStoredClickIds('/signup')); }}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: theme.primaryContrast,
                    backgroundColor: theme.primary,
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Get Started
                </button>
              </>
            )}
          </div>
        )}
      </nav>

      {/* Mobile nav overlay */}
      {isMobile && navOpen && (
        <div
          role="dialog"
          aria-label="Menu"
          style={{
            position: 'fixed',
            inset: 0,
            top: 68,
            zIndex: 99,
            backgroundColor: theme.card,
            borderTop: `1px solid ${theme.border}`,
            padding: '20px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            overflow: 'auto',
          }}
        >
          {(loading ? false : isAuthenticated) ? (
            <>
              <button
                onClick={() => { navigate('/profile'); setNavOpen(false); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '14px 16px',
                  fontSize: '16px',
                  fontWeight: '500',
                  color: theme.text,
                  backgroundColor: theme.primaryLight,
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <User size={20} color={theme.primary} />
                {displayName}
              </button>
              <button
                onClick={() => { navigate('/dashboard'); setNavOpen(false); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '14px 16px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: theme.primaryContrast,
                  backgroundColor: theme.primary,
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <LayoutDashboard size={20} />
                Dashboard
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => { navigate('/login'); setNavOpen(false); }}
                style={{
                  padding: '14px 16px',
                  fontSize: '16px',
                  fontWeight: '500',
                  color: theme.text,
                  backgroundColor: 'transparent',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                Log In
              </button>
              <button
                onClick={() => { navigate(withStoredClickIds('/signup')); setNavOpen(false); }}
                style={{
                  padding: '14px 16px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: theme.primaryContrast,
                  backgroundColor: theme.primary,
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                Get Started
              </button>
            </>
          )}
        </div>
      )}

      <main style={{ flex: 1 }}>
      {/* Hero Section */}
      <section aria-label="Hero" style={{
        padding: isMobile ? MOB.heroPadding : '100px 40px',
        textAlign: 'center',
        background: darkMode
          ? `linear-gradient(135deg, ${theme.primaryMuted}22 0%, ${theme.bg} 50%, ${theme.primaryLight}22 100%)`
          : `linear-gradient(135deg, ${theme.primaryLight} 0%, ${theme.bg} 50%, ${theme.accentLight} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 0,
            background: `
              radial-gradient(circle at 12% 18%, ${theme.primary}22 0%, transparent 36%),
              radial-gradient(circle at 86% 22%, ${theme.accent}22 0%, transparent 30%),
              radial-gradient(circle at 74% 80%, ${theme.primary}16 0%, transparent 28%)
            `,
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 0,
            opacity: darkMode ? 0.2 : 0.28,
            backgroundImage: `
              linear-gradient(${theme.border} 1px, transparent 1px),
              linear-gradient(90deg, ${theme.border} 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
            maskImage: 'radial-gradient(circle at center, black 45%, transparent 95%)',
          }}
        />
        <h1 style={{
          fontSize: isMobile ? MOB.heroTitleSize : '56px',
          fontWeight: '800',
          color: theme.text,
          margin: '0 0 24px 0',
          lineHeight: '1.15',
          maxWidth: '900px',
          marginLeft: 'auto',
          marginRight: 'auto',
          position: 'relative',
          zIndex: 1,
        }}>
          AI Competitor Analysis for<br />
          <span style={{ color: theme.primary }}>Real-Time Market Intelligence.</span>
        </h1>

        <p style={{
          fontSize: isMobile ? MOB.heroSubSize : '20px',
          color: theme.textMuted,
          maxWidth: '600px',
          margin: '0 auto 40px auto',
          lineHeight: '1.6',
          position: 'relative',
          zIndex: 1,
        }}>
          AI-powered competitor research that gives you pricing history, 
          sentiment analysis, and market insights in seconds. 
          Not hours.
        </p>

        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: '12px',
          justifyContent: 'center',
          alignItems: 'stretch',
          position: 'relative',
          zIndex: 1,
        }}>
          {!loading && isAuthenticated ? (
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                padding: isMobile ? '14px 24px' : '16px 32px',
                fontSize: isMobile ? '15px' : '16px',
                fontWeight: '600',
                color: theme.primaryContrast,
                backgroundColor: theme.primary,
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: isMobile ? '100%' : undefined,
              }}
            >
              Go to Dashboard
              <ArrowRight size={18} />
            </button>
          ) : (
            <button
              onClick={() => { trackGoogleAdsDiagnostic('signup_cta_click', { location: 'landing_page' }); navigate(withStoredClickIds('/signup')); }}
              style={{
                padding: isMobile ? '14px 24px' : '16px 32px',
                fontSize: isMobile ? '15px' : '16px',
                fontWeight: '600',
                color: theme.primaryContrast,
                backgroundColor: theme.primary,
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: isMobile ? '100%' : undefined,
              }}
            >
              Create Account
              <ArrowRight size={18} />
            </button>
          )}
          <button
            onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
            style={{
              padding: isMobile ? '14px 24px' : '16px 32px',
              fontSize: isMobile ? '15px' : '16px',
              fontWeight: '600',
              color: theme.text,
              backgroundColor: theme.card,
              border: `1px solid ${theme.border}`,
              borderRadius: '8px',
              cursor: 'pointer',
              width: isMobile ? '100%' : undefined,
            }}
          >
            See Features
          </button>
        </div>

        {/* Trust Badges */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: isMobile ? '16px' : '40px',
          marginTop: isMobile ? '36px' : '60px',
          padding: isMobile ? '16px' : '24px',
          position: 'relative',
          zIndex: 1,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Shield size={20} color={theme.accent} />
            <span style={{ fontSize: '14px', color: theme.textMuted }}>Secure & Private</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Zap size={20} color={theme.usageWarn} />
            <span style={{ fontSize: '14px', color: theme.textMuted }}>Lightning Fast</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Users size={20} color={theme.primary} />
            <span style={{ fontSize: '14px', color: theme.textMuted }}>Active Development</span>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" aria-label="Features" style={{
        padding: isMobile ? MOB.sectionPadding : '100px 40px',
        backgroundColor: theme.bg,
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '32px' : '60px' }}>
            <h2 style={{
              fontSize: isMobile ? MOB.sectionTitleSize : '36px',
              fontWeight: '700',
              color: theme.text,
              margin: '0 0 16px 0',
            }}>
              Everything You Need to Win
            </h2>
            <p style={{
              fontSize: isMobile ? '15px' : '18px',
              color: theme.textMuted,
              maxWidth: '600px',
              margin: '0 auto',
            }}>
              Powerful tools that give you the competitive edge
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: isMobile ? '16px' : '24px',
          }}>
            {features.map((feature, idx) => {
              const isHovered = hoveredCard === idx;
              const Icon = feature.icon;
              
              return (
                <div
                  key={idx}
                  ref={el => cardRefs.current[idx] = el}
                  onMouseEnter={() => setHoveredCard(idx)}
                  onMouseLeave={() => setHoveredCard(null)}
                  onMouseMove={(e) => {
                    const rect = cardRefs.current[idx]?.getBoundingClientRect();
                    if (rect) {
                      setMousePosition({
                        x: ((e.clientX - rect.left) / rect.width) * 100,
                        y: ((e.clientY - rect.top) / rect.height) * 100
                      });
                    }
                  }}
                  style={{
                    padding: isMobile ? MOB.cardPadding : '32px',
                    backgroundColor: theme.card,
                    borderRadius: '16px',
                    border: `1px solid ${isHovered ? feature.color : theme.border}`,
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transform: isHovered ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
                    boxShadow: isHovered 
                      ? `0 20px 40px -10px ${feature.color}40, 0 0 0 1px ${feature.color}30`
                      : `0 4px 6px -1px ${theme.border}`,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  {/* Animated gradient background on hover */}
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: isHovered 
                      ? `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, ${feature.color}15 0%, transparent 50%)`
                      : 'transparent',
                    transition: 'background 0.3s ease',
                    pointerEvents: 'none',
                  }} />
                  
                  {/* Corner accent */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: isHovered ? '100px' : '60px',
                    height: isHovered ? '100px' : '60px',
                    background: `linear-gradient(135deg, transparent 50%, ${feature.color}20 50%)`,
                    borderRadius: '0 16px 0 0',
                    transition: 'all 0.4s ease',
                    pointerEvents: 'none',
                  }} />
                  
                  {/* Icon container with animation */}
                  <div style={{
                    width: '56px',
                    height: '56px',
                    backgroundColor: isHovered ? feature.color : theme.primaryLight,
                    borderRadius: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isHovered ? '#ffffff' : feature.color,
                    marginBottom: '20px',
                    position: 'relative',
                    transform: isHovered ? 'scale(1.1) rotate(-5deg)' : 'scale(1) rotate(0)',
                    boxShadow: isHovered ? `0 8px 20px -4px ${feature.color}50` : 'none',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}>
                    <Icon size={26} strokeWidth={2} />
                    
                    {/* Pulse ring animation on hover */}
                    {isHovered && (
                      <div style={{
                        position: 'absolute',
                        inset: '-4px',
                        borderRadius: '18px',
                        border: `2px solid ${feature.color}40`,
                        animation: 'pulse-ring 1.5s ease-out infinite',
                      }} />
                    )}
                  </div>
                  
                  {/* Stats badge */}
                  <div style={{
                    position: 'absolute',
                    top: '24px',
                    right: '24px',
                    padding: '4px 10px',
                    backgroundColor: isHovered ? `${feature.color}20` : theme.primaryLight,
                    borderRadius: '20px',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: isHovered ? feature.color : theme.primary,
                    opacity: isHovered ? 1 : 0.7,
                    transform: isHovered ? 'translateY(0)' : 'translateY(-4px)',
                    transition: 'all 0.3s ease',
                  }}>
                    {feature.stats}
                  </div>
                  
                  {/* Title */}
                  <h3 style={{
                    fontSize: '19px',
                    fontWeight: '700',
                    color: theme.text,
                    margin: '0 0 10px 0',
                    transform: isHovered ? 'translateX(4px)' : 'translateX(0)',
                    transition: 'transform 0.3s ease',
                  }}>
                    {feature.title}
                  </h3>
                  
                  {/* Description */}
                  <p style={{
                    fontSize: '14px',
                    color: theme.textMuted,
                    margin: 0,
                    lineHeight: '1.6',
                    transform: isHovered ? 'translateX(4px)' : 'translateX(0)',
                    transition: 'transform 0.3s ease 0.05s',
                  }}>
                    {feature.description}
                  </p>
                  
                  {/* Bottom line accent */}
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: isHovered 
                      ? `linear-gradient(90deg, ${feature.color}, ${feature.color}80)` 
                      : 'transparent',
                    borderRadius: '0 0 16px 16px',
                    transition: 'all 0.4s ease',
                  }} />
                </div>
              );
            })}
          </div>
          
          {/* CSS for animations */}
          <style>{`
            @keyframes pulse-ring {
              0% {
                transform: scale(1);
                opacity: 1;
              }
              100% {
                transform: scale(1.3);
                opacity: 0;
              }
            }
          `}</style>
        </div>
      </section>

      {/* Insights Snapshot (between feature cards and pricing cards) */}
      <section aria-label="Key benefits" style={{
        padding: isMobile ? '0 20px 48px' : '0 40px 70px',
        backgroundColor: theme.bg,
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
        }}>
          <div style={{
            maxWidth: '860px',
            margin: '0 auto',
            padding: '16px 20px',
            borderRadius: '12px',
            backgroundColor: theme.primaryLight,
            border: `1px solid ${theme.primaryMuted}`,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '12px',
            textAlign: 'left',
          }}>
            <div>
              <p style={{ margin: 0, fontSize: '12px', fontWeight: '700', color: theme.primary, letterSpacing: '0.03em' }}>SEO + Visibility</p>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: theme.text }}>Spot ranking gaps and content opportunities fast.</p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '12px', fontWeight: '700', color: theme.primary, letterSpacing: '0.03em' }}>Pricing + Sentiment</p>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: theme.text }}>See where competitors win on price and perception.</p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '12px', fontWeight: '700', color: theme.primary, letterSpacing: '0.03em' }}>Action-Ready Output</p>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: theme.text }}>Export reports your team can use immediately.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section aria-label="Pricing" style={{
        padding: isMobile ? MOB.sectionPadding : '100px 40px',
        backgroundColor: theme.bg,
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{
            marginBottom: '24px',
            padding: isMobile ? '12px 16px' : '14px 20px',
            backgroundColor: theme.primaryLight,
            border: `1px solid ${theme.primaryMuted}`,
            borderRadius: '12px',
            fontSize: '14px',
            color: theme.text,
            lineHeight: 1.5,
          }}>
            <strong>Beta build.</strong> If you think something went wrong (e.g. missing credits, something wasted your credits, or a billing issue), <button type="button" onClick={() => openContactForm({ subject: 'Beta / Support – credits or billing' })} style={{ color: theme.primary, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit', textDecoration: 'underline' }}>contact support</button> for extra credits or help.
          </div>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '28px' : '60px' }}>
            <h2 style={{
              fontSize: isMobile ? MOB.sectionTitleSize : '36px',
              fontWeight: '700',
              color: theme.text,
              margin: '0 0 16px 0',
            }}>
              Simple, Transparent Pricing
            </h2>
            <p style={{
              fontSize: isMobile ? '15px' : '18px',
              color: theme.textMuted,
              maxWidth: '600px',
              margin: '0 auto',
            }}>
              Simple, transparent pricing with plans that grow alongside your research needs.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: isMobile ? '20px' : '28px',
            maxWidth: '1200px',
            margin: '0 auto',
          }}>
            {/* Pro Plan - Basic monthly */}
            <div
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                e.currentTarget.style.boxShadow = `0 25px 50px -12px ${theme.primary}40`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              style={{
                padding: '32px',
                backgroundColor: theme.card,
                borderRadius: '20px',
                border: `2px solid ${theme.primary}`,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <div style={{
                position: 'absolute',
                top: '-12px',
                left: '50%',
                transform: 'translateX(-50%)',
                padding: '6px 16px',
                backgroundColor: theme.primary,
                color: theme.primaryContrast,
                fontSize: '12px',
                fontWeight: '600',
                borderRadius: '20px',
              }}>
                MOST POPULAR
              </div>
              <h3 style={{ fontSize: '22px', fontWeight: '700', color: theme.text, margin: '0 0 6px 0' }}>Pro</h3>
              <p style={{ fontSize: '13px', color: theme.textMuted, margin: '0 0 20px 0' }}>For serious market researchers</p>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                  <span style={{ fontSize: '42px', fontWeight: '800', color: theme.text }}>$30</span>
                  <span style={{ fontSize: '15px', color: theme.textMuted }}>/month</span>
                </div>
              </div>
              <button onClick={() => { trackGoogleAdsDiagnostic('signup_cta_click', { location: 'landing_page' }); navigate(withStoredClickIds('/signup')); }} style={{ width: '100%', padding: '12px', fontSize: '15px', fontWeight: '600', color: theme.primaryContrast, backgroundColor: theme.primary, border: 'none', borderRadius: '8px', cursor: 'pointer', marginBottom: '24px' }}>
                Create Account
              </button>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {pricingFeatures.map((f, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Check size={14} color={theme.accent} style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: '13px', color: theme.text }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pro Annual */}
            <div 
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-12px)';
                e.currentTarget.style.borderColor = theme.accent;
                e.currentTarget.style.boxShadow = `0 20px 40px -10px ${theme.accent}30`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = theme.border;
                e.currentTarget.style.boxShadow = 'none';
              }}
              style={{
                padding: '32px',
                backgroundColor: theme.card,
                borderRadius: '20px',
                border: `1px solid ${theme.border}`,
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <Calendar size={20} color={theme.primary} />
                <h3 style={{ fontSize: '22px', fontWeight: '700', color: theme.text, margin: 0 }}>Pro Annual</h3>
              </div>
              <p style={{ fontSize: '13px', color: theme.textMuted, margin: '0 0 20px 0' }}>12 months of Pro billing, paid once per year.</p>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '42px', fontWeight: '800', color: theme.text }}>$25.50</span>
                  <span style={{ fontSize: '15px', color: theme.textMuted }}>/month</span>
                </div>
                <p style={{ fontSize: '12px', color: theme.accent, fontWeight: '600', margin: '6px 0 0 0' }}>$306 billed annually</p>
              </div>
              <button onClick={() => { trackGoogleAdsDiagnostic('signup_cta_click', { location: 'landing_page' }); navigate(withStoredClickIds('/signup')); }} style={{ width: '100%', padding: '12px', fontSize: '15px', fontWeight: '600', color: theme.primaryContrast, backgroundColor: theme.primary, border: 'none', borderRadius: '8px', cursor: 'pointer', marginBottom: '24px' }}>
                Get Annual Plan
              </button>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {pricingFeatures.map((f, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Check size={14} color={theme.accent} style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: '13px', color: theme.text }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Growth */}
            <div 
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-12px)';
                e.currentTarget.style.borderColor = '#f59e0b';
                e.currentTarget.style.boxShadow = `0 20px 40px -10px rgba(245, 158, 11, 0.3)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = theme.border;
                e.currentTarget.style.boxShadow = 'none';
              }}
              style={{
                padding: '32px',
                backgroundColor: theme.card,
                borderRadius: '20px',
                border: `1px solid ${theme.border}`,
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <h3 style={{ fontSize: '22px', fontWeight: '700', color: theme.text, margin: '0 0 6px 0' }}>Growth</h3>
              <p style={{ fontSize: '13px', color: theme.textMuted, margin: '0 0 20px 0' }}>4× as many queries as Pro</p>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                  <span style={{ fontSize: '42px', fontWeight: '800', color: theme.text }}>$60</span>
                  <span style={{ fontSize: '15px', color: theme.textMuted }}>/month</span>
                </div>
              </div>
              <button onClick={() => { trackGoogleAdsDiagnostic('signup_cta_click', { location: 'landing_page' }); navigate(withStoredClickIds('/signup')); }} style={{ width: '100%', padding: '12px', fontSize: '15px', fontWeight: '600', color: theme.primaryContrast, backgroundColor: theme.primary, border: 'none', borderRadius: '8px', cursor: 'pointer', marginBottom: '24px' }}>
                Create Account
              </button>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {pricingFeatures.map((f, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Check size={14} color={theme.primary} style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: '13px', color: theme.text }}>{f}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
                  <Check size={14} color={theme.primary} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: theme.text, fontWeight: '600' }}>4× monthly research queries</span>
                </div>
              </div>
            </div>

            {/* Single Intelligence Report - One-time $79 */}
            <div
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-12px)';
                e.currentTarget.style.borderColor = '#06b6d4';
                e.currentTarget.style.boxShadow = `0 20px 40px -10px rgba(6, 182, 212, 0.3)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = theme.border;
                e.currentTarget.style.boxShadow = 'none';
              }}
              style={{
                padding: '32px',
                backgroundColor: theme.card,
                borderRadius: '20px',
                border: `1px solid ${theme.border}`,
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <FileText size={20} color="#06b6d4" />
                <h3 style={{ fontSize: '22px', fontWeight: '700', color: theme.text, margin: 0 }}>Single Intelligence Report</h3>
              </div>
              <p style={{ fontSize: '13px', color: theme.textMuted, margin: '0 0 20px 0' }}>
                We work with you to determine your market and provide a comprehensive, one-time deep dive report. No subscription required.
              </p>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                  <span style={{ fontSize: '42px', fontWeight: '800', color: theme.text }}>$79</span>
                  <span style={{ fontSize: '15px', color: theme.textMuted }}>one-time</span>
                </div>
              </div>
              <button
                onClick={() => isAuthenticated ? setShowReportIntake(true) : navigate('/signup')}
                style={{ width: '100%', padding: '12px', fontSize: '15px', fontWeight: '600', color: theme.primaryContrast, backgroundColor: '#06b6d4', border: 'none', borderRadius: '8px', cursor: 'pointer', marginBottom: '24px' }}
              >
                Get Report
              </button>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Check size={14} color={theme.accent} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: theme.text }}>Price history</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Check size={14} color={theme.accent} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: theme.text }}>Market gaps</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Check size={14} color={theme.accent} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: theme.text }}>Competitor analysis</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Check size={14} color={theme.accent} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: theme.text }}>SEO research</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Check size={14} color={theme.accent} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: theme.text }}>Market trends</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Check size={14} color={theme.accent} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: theme.text }}>Sentiment analysis</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Check size={14} color={theme.accent} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: theme.text }}>Full dashboard suite of insights</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
                  <Check size={14} color="#06b6d4" style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: theme.text, fontWeight: '600' }}>Delivered in interactive HTML format</span>
                </div>
              </div>
            </div>

            {/* Enterprise - Contact for quote */}
            <div 
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-12px)';
                e.currentTarget.style.borderColor = theme.textMuted;
                e.currentTarget.style.boxShadow = `0 20px 40px -10px rgba(0, 0, 0, 0.2)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = theme.border;
                e.currentTarget.style.boxShadow = 'none';
              }}
              style={{
                padding: '32px',
                backgroundColor: theme.card,
                borderRadius: '20px',
                border: `1px solid ${theme.border}`,
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <Building2 size={20} color={theme.textMuted} />
                <h3 style={{ fontSize: '22px', fontWeight: '700', color: theme.text, margin: 0 }}>Enterprise</h3>
              </div>
              <p style={{ fontSize: '13px', color: theme.textMuted, margin: '0 0 20px 0' }}>Custom bulk subscriptions for your company</p>
              <div style={{ marginBottom: '20px', minHeight: '72px' }}>
                <p style={{ fontSize: '15px', color: theme.text, fontWeight: '600', margin: 0 }}>Contact us for a custom quote</p>
                <p style={{ fontSize: '13px', color: theme.textMuted, margin: '6px 0 0 0' }}>Volume discounts, SSO, dedicated support</p>
              </div>
              <button
                onClick={() => openContactForm({ subject: 'Enterprise subscription inquiry' })}
                style={{ width: '100%', padding: '12px', fontSize: '15px', fontWeight: '600', color: theme.textMuted, backgroundColor: theme.card, border: `1px solid ${theme.border}`, borderRadius: '8px', cursor: 'pointer', marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                <Mail size={18} />
                Contact Sales
              </button>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Check size={14} color={theme.accent} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: theme.text }}>Everything in Growth</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Check size={14} color={theme.accent} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: theme.text }}>Custom usage & seat limits</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Check size={14} color={theme.accent} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: theme.text }}>Dedicated support</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Check size={14} color={theme.accent} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: theme.text }}>SSO & compliance (on request)</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      </main>

      {/* Footer */}
      <footer style={{
        padding: isMobile ? MOB.footerPadding : '40px',
        backgroundColor: theme.card,
        borderTop: `1px solid ${theme.border}`,
        color: theme.text,
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'center' : 'center',
          flexWrap: 'wrap',
          gap: isMobile ? '20px' : '24px',
          textAlign: isMobile ? 'center' : undefined,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src="/logo.webp" width="160" height="149" alt="Market Scan" style={{ width: isMobile ? 44 : 56, height: isMobile ? 44 : 56, objectFit: 'contain', borderRadius: '8px', display: 'block', backgroundColor: 'transparent' }} />
            <span style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '700', color: theme.text }}>
              Market Scan
            </span>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? '20px' : '32px', justifyContent: isMobile ? 'center' : undefined }}>
            <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" style={{ color: theme.textMuted, textDecoration: 'none', fontSize: '14px' }}>Privacy Policy</a>
            <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noopener noreferrer" style={{ color: theme.textMuted, textDecoration: 'none', fontSize: '14px' }}>Terms of Service</a>
            <button type="button" onClick={() => openContactForm()} style={{ color: theme.textMuted, textDecoration: 'none', fontSize: '14px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit' }}>Contact</button>
            <PrivacyChoicesButton style={{ color: theme.textMuted, fontSize: '14px' }} />
          </div>

          <p style={{ fontSize: '14px', color: theme.textMuted, margin: 0 }}>
            © 2026 Market Scan. All rights reserved.
          </p>
          <p style={{ fontSize: '12px', color: theme.textMuted, margin: '8px 0 0 0', width: '100%', textAlign: 'center' }}>
            By using our services you agree to our{' '}
            <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noopener noreferrer" style={{ color: theme.primary, textDecoration: 'none' }}>Terms of Service</a>
            {' '}and{' '}
            <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" style={{ color: theme.primary, textDecoration: 'none' }}>Privacy Policy</a>.
          </p>
          <p style={{ fontSize: '12px', color: theme.textMuted, margin: '8px 0 0 0', width: '100%', textAlign: 'center' }}>
            Developed, maintained, and owned by{' '}
            <a href="https://pdxintelligence.com" target="_blank" rel="noopener noreferrer" style={{ color: theme.primary, textDecoration: 'none' }}>PDX Intelligence</a>
          </p>
        </div>
      </footer>

      <ReportIntakeModal open={showReportIntake} onClose={() => setShowReportIntake(false)} />
    </div>
  );
}

export default LandingPage;
